<template>
  <div ref="formRef" class="asset-prices-form" :class="mainClasses">
    <div class="asset-prices-form__input">
      <UIInputDate
        v-model="formData.date"
        placeholder="Date"
        :size
        :disabled
      />
    </div>
    <div class="asset-prices-form__input">
      <UIInputNumber
        v-model="formData.close"
        data-refid="assetPricesFormClose"
        :disabled
        :placeholder
        :size
        @click:enter="handleSubmit"
        @ready="onReady"
      />
    </div>
    <div class="asset-prices-form__input--source">
      <UIInputSelect
        v-model="formData.source"
        v-tooltip="{
          content: SOURCE_TOOLTIP,
          html: true
        }"
        :data="ASSET_PRICE_SOURCE_EDITABLE"
        :size
        :disabled
        placeholder="Source"
      />
    </div>
    <UIButton
      data-refid="assetPricesFormAdd"
      v-bind="buttonProps"
      :size
      class="asset-prices-form__icon-button"
      @click="handleSubmit"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, ref, useTemplateRef } from 'vue'
import { useResizeObserver } from '@vueuse/core'

import { ButtonSize } from '@types'
import { AssetPriceForm } from './utils/types'
import { AssetPriceSource } from './utils/enums'

import { ASSET_PRICE_SOURCE_EDITABLE, SOURCE_TOOLTIP } from './utils/const'

import { getCurrentDate } from '@/helpers/dates'

import { PlusIcon } from '@heroicons/vue/24/outline'
import { UIButton } from '@ui/buttons'
import { UIInputDate, UIInputNumber, UIInputSelect } from '@ui/fields'

const COLLAPSED_WIDTH = 400

type Props = {
  disabled?: boolean
  placeholder?: string
  label?: string
  defaultDate?: string
  size?: ButtonSize
}

type Emits = {
  submit: [data: AssetPriceForm]
}

const {
  placeholder = 'Close',
  disabled,
  label,
  defaultDate = getCurrentDate({}),
  size = 'sm',
} = defineProps<Props>()
const emit = defineEmits<Emits>()

defineExpose({
  getCloseValue: () => formData.close,
})

const formRef = useTemplateRef('formRef')
const formWidth = ref(0)

useResizeObserver(formRef, entries => {
  const { width } = entries[0].contentRect
  formWidth.value = width
})

const closeFieldRef = ref<HTMLInputElement>()

const formData = reactive<AssetPriceForm>({
  date: defaultDate,
  close: null,
  source: AssetPriceSource.MANUAL,
})

const mainClasses = computed(() => ({
  'asset-prices-form--collapsed': formWidth.value < COLLAPSED_WIDTH,
}))

const isButtonDisabled = computed(
  () => disabled || !formData.date || formData.close === null,
)

const buttonProps = computed(() => ({
  disabled: isButtonDisabled.value,
  leading: label ? undefined : PlusIcon,
  label: label ?? 'Price',
}))

const handleSubmit = () => {
  emit('submit', { ...formData })
  formData.close = null
  closeFieldRef.value?.focus()
}

const onReady = async (e: HTMLInputElement) => {
  closeFieldRef.value = e
  closeFieldRef.value?.focus()
}
</script>

<style scoped>
.asset-prices-form {
  @apply grid grid-cols-[1fr_0.75fr_6rem_min-content];
  @apply gap-2;

  &--collapsed {
    @apply grid-cols-[1fr_1fr];
  }

  &__icon-button {
    :deep() .ui-button__icon {
      @apply !w-4;
    }
  }
}
</style>
